import { useState } from 'react'
import { Trans } from 'react-i18next'
import { v4 as uuid4 } from 'uuid'

import { IActionFormProps } from '@makyoapp/makyo-common'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant } from '@makyoapp/makyo-ui'

import { useForm } from '@/lib/use-form'

import { PATH_COLORS } from '../../action-workflows/constants/action-color'
import { SwitchConditionRepeater } from '../../action-workflows/SwitchConditionRepeater'
import { SwitchCase, SwitchFormData } from './types'

export default function ActFormSwitch({ onSubmit, onDismiss, initialData }: IActionFormProps) {
  const [selectedCaseIndex, setSelectedCaseIndex] = useState<number | null>(null)
  const [editingPathName, setEditingPathName] = useState<string | null>(null)

  const { form, onSubmit: onFormSubmit } = useForm({
    defaultValues: {
      cases: initialData?.cases || [
        {
          id: uuid4(),
          name: `Path ${1}`,
          condition: {
            isAny: true,
            conditions: [],
          },
        },
      ],
    },
  })

  const handleSubmit = onFormSubmit((data: SwitchFormData) => {
    const normalizedData = {
      ...data,
      cases: data.cases.map((caseItem, index) => ({
        ...caseItem,
        name: caseItem.name || `Path ${index + 1}`,
      })),
    }
    onSubmit(normalizedData)
  })

  const handleAddCase = () => {
    const currentCases = form.getValues('cases')
    const newCase: SwitchCase = {
      id: uuid4(),
      name: `Path ${currentCases.length + 1}`,
      condition: {
        isAny: true,
        conditions: [],
      },
    }
    form.setValues('cases', [...currentCases, newCase])
  }

  const handleRemoveCase = (index: number) => {
    const currentCases = form.getValues('cases')
    const updatedCases = currentCases.filter((_: SwitchCase, i: number) => i !== index)
    form.setValues('cases', updatedCases)
    if (selectedCaseIndex === index) {
      setSelectedCaseIndex(null)
    } else if (selectedCaseIndex && selectedCaseIndex > index) {
      setSelectedCaseIndex(selectedCaseIndex - 1)
    }
  }

  const handleCaseConditionChange = (index: number, condition: any) => {
    const currentCases = form.getValues('cases')
    const updatedCases = [...currentCases]
    updatedCases[index] = { ...updatedCases[index], condition }
    form.setValues('cases', updatedCases)
  }

  const updateCaseName = (index: number, name: string) => {
    const currentCases = form.getValues('cases')
    const updatedCases = [...currentCases]
    updatedCases[index] = { ...updatedCases[index], name }
    form.setValues('cases', updatedCases)
  }

  const startEditingPathName = (caseId: string) => {
    setEditingPathName(caseId)
  }

  const currentCases = form.getValues('cases')

  const invalidForm = currentCases.length === 0 || !!form.errors

  // Colors for different paths (cycling through)

  return (
    <div className='flex flex-col h-full md:min-w-[768px]'>
      {/* Header */}
      <div className='border-b border-gray-200 dark:border-gray-700 p-6'>
        <h3 className='text-xl font-semibold text-gray-900 dark:text-white'>
          <Trans>Configure Switch Node</Trans>
        </h3>
        <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
          <Trans>Set up multiple conditional paths for your workflow</Trans>
        </p>
      </div>

      {/* Content */}
      <div className='flex-1 p-6'>
        <div className='space-y-6'>
          <div className='flex justify-between items-center'>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300'>
              <Trans>Switch Cases</Trans>
            </label>
          </div>
          <div className='bg-gray-50 dark:bg-gray-700 rounded-lg p-4'>
            <div className='space-y-3 mk-scroll'>
              {currentCases.map((caseItem: SwitchCase, index: number) => {
                const color = PATH_COLORS[index % PATH_COLORS.length]
                const caseName = caseItem.name || `Path ${index + 1}`

                return (
                  <div key={caseItem.id} className='space-y-4'>
                    <SwitchConditionRepeater
                      id={`switch-case-${caseItem.id}`}
                      value={caseItem.condition}
                      onChange={(condition) => handleCaseConditionChange(index, condition)}
                      pathName={caseName}
                      pathColor={color}
                      onPathNameChange={(name) => updateCaseName(index, name)}
                      onPathNameEdit={() => startEditingPathName(caseItem.id)}
                      isEditingPathName={editingPathName === caseItem.id}
                      onRemoveCase={() => handleRemoveCase(index)}
                      canRemove={currentCases.length > 1}
                      actions={initialData?.actionOptions}
                    />
                  </div>
                )
              })}
            </div>
            <Button
              variant={ButtonVariant.Ghost}
              onClick={handleAddCase}
              className='mt-2 justify-center hover:dark:bg-gray-700 hover:bg-gray-50 hover:text-teal-500 cursor-pointer pl-0!'
              icon={<MaterialIcon icon={'add'} />}
            >
              <Trans>Add Case</Trans>
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='border-t border-gray-200 dark:border-gray-700 p-6 flex justify-end gap-3 bg-gray-50 dark:bg-transparent'>
        <Button
          variant={ButtonVariant.Outlined}
          onClick={onDismiss}
          className='text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border-gray-300 dark:border-gray-600'
        >
          <Trans>Cancel</Trans>
        </Button>
        <Button onClick={handleSubmit} disabled={invalidForm}>
          <Trans>Save Switch</Trans>
        </Button>
      </div>
    </div>
  )
}
